import type { BeckyModel, BeckyRequestPayload, Translations } from '@wix/thunderbolt-becky-root'
import {
	deStringifyExperiments,
	getMergedDataWithFlatPageStructure,
	getCompsInFirstFold,
	HEURISTIC_FIRST_FOLD_HEIGHT_DESKTOP,
	HEURISTIC_FIRST_FOLD_HEIGHT_MOBILE,
	geResponsiveCompsInFirstFold,
	getRequestedPageCompId,
} from '@wix/thunderbolt-becky-root'
import { BeckyTopology, FetchFn, MetricsReporter } from '@wix/thunderbolt-becky-types'
import { mergeMappers } from '@wix/thunderbolt-components/src/mappers'
import { getComponentMappers } from './componentsSiteAssets'
import { applyAnywhereThemeOverride } from './themeOverrides'
import { getTranslation } from './translationsUrl'

export type ResolveBeckyModelParams = {
	payload: BeckyRequestPayload
	fetchFn: FetchFn
	beckyTopology: BeckyTopology
	shapeStore?: Record<string, string>
	metricsReporter: MetricsReporter
}

export const resolveBeckyModel = async ({
	payload,
	beckyTopology: { publicTopology, environmentTopology },
	fetchFn,
	shapeStore = {},
	metricsReporter,
}: ResolveBeckyModelParams): Promise<BeckyModel> => {
	const {
		pages,
		viewMode,
		beckyExperiments,
		isWixCodeOnPage: isWixCodeOnPageParam,
		isWixCodeOnSite: isWixCodeOnSiteParam,
		hasTPAWorkerOnSite: hasTPAWorkerOnSiteParam,
		freemiumBanner: freemiumBannerParam,
		coBrandingBanner: coBrandingBannerParam,
		dayfulBanner: dayfulBannerParam,
		mobileActionsMenu: mobileActionsMenuParam,
		externalBaseUrl,
		metaSiteId,
		siteId,
		clientSpecMap,
		language,
		originalLanguage,
		isWixSite,
		languageResolutionMethod,
		isMultilingualEnabled,
		useSandboxInHTMLComp,
		routersInfo,
		deviceType,
		qaMode,
		testMode,
		debugMode,
		isInSeo: isInSeoParam,
		isPremiumDomain,
		migratingToOoiWidgetIds,
		checkoutOOI,
		stylableItemsCss,
		stylableInstance,
		registryLibrariesTopology,
		isHttps,
		anywhereThemeOverride,
		seoTranslations,
		formFactor,
		disableStaticPagesUrlHierarchy,
		aboveTheFoldSectionsNum,
		isConsentPolicyActive,
	} = payload

	const isResponsive = payload.isResponsive === 'true'
	const experiments = deStringifyExperiments(beckyExperiments)

	const [processedPages, componentsMappers] = await Promise.all([
		getMergedDataWithFlatPageStructure(pages, viewMode, isResponsive),
		getComponentMappers({
			fetchFn,
			registryLibrariesTopology,
			metricsReporter,
		}),
	])

	const data = anywhereThemeOverride
		? applyAnywhereThemeOverride({ data: processedPages.data, anywhereThemeOverride })
		: processedPages.data

	const isInSeo = isInSeoParam === 'true'

	const siteStructure = { ...processedPages.structure, ...processedPages.masterPageStructure }

	const compsInFoldThreshold =
		!viewMode || viewMode === 'desktop' ? HEURISTIC_FIRST_FOLD_HEIGHT_DESKTOP : HEURISTIC_FIRST_FOLD_HEIGHT_MOBILE
	const compsInFoldMap = isResponsive
		? (experiments['specs.thunderbolt.edixIsInFirstFold'] &&
				geResponsiveCompsInFirstFold(
					processedPages,
					getRequestedPageCompId(pages),
					processedPages.data.layout_data,
					processedPages.data.variants_data,
					aboveTheFoldSectionsNum
				)) ||
		  {}
		: getCompsInFirstFold(siteStructure, compsInFoldThreshold)

	const globalTranslations: Translations = await getTranslation(language!)

	return {
		...processedPages,
		data,
		seoTranslations,
		externalBaseUrl,
		metaSiteId,
		siteId,
		previousStore: new WeakMap(),
		viewMode: viewMode || 'desktop',
		environmentTopology,
		publicTopology,
		language: language!,
		isMultilingualEnabled: isMultilingualEnabled === 'true',
		disableStaticPagesUrlHierarchy: disableStaticPagesUrlHierarchy === 'true',
		languageResolutionMethod,
		originalLanguage,
		useSandboxInHTMLComp: useSandboxInHTMLComp === 'true',
		isResponsive,
		isWixSite: isWixSite === 'true',
		hasTPAWorkerOnSite: hasTPAWorkerOnSiteParam === 'true',
		fetchRequestsStore: {},
		afterFirstRecalculate: false,
		isWixCodeOnPage: isWixCodeOnPageParam === 'true',
		isWixCodeOnSite: isWixCodeOnSiteParam === 'true',
		renderFlags: {
			isWixAdsAllowed: freemiumBannerParam === 'true',
			isWixBlocks: false,
			showScreenInComp: isInSeo,
			componentViewMode: 'preview',
			componentPreviewStates: {},
			isBackToTopButtonAllowed: false,
			shouldBlockGoogleMapInteraction: false,
			shouldResetTinyMenuZIndex: false,
			shouldResetSlideShowNextPrevButtonsPosition: false,
			shouldResetSubscribeFormMinMaxWidth: false,
			shouldUpdateJsonFromMeasureMap: false,
			isPlayingAllowed: false,
			isSocialInteractionAllowed: false,
			isExternalNavigationAllowed: true,
			isSlideShowGalleryClickAllowed: true,
			renderFixedPositionContainers: true,
			shouldResetGalleryToOriginalState: false,
			shouldResetComponent: true,
			siteScale: 1,
			isComponentTransitionAllowed: true,
			allowShowingFixedComponents: true,
			isZoomAllowed: false,
			renderFixedPositionBackgrounds: false,
			extraSiteHeight: 0,
			ignoreComponentsHiddenProperty: [],
			ignoreComponentsCollapsedProperty: [],
			showHiddenComponents: false,
			blockingLayer: null,
			allowSiteOverflow: true,
			renderScrollSnap: true,
			renderSticky: true,
			compsToRenderSticky: {},
			enablePageAutoGrowForDetachedComponents: true,
			shouldRenderTPAsIframe: true,
			isAdi: false,
			isLivePreviewOpenForEditor: true,
		},
		meshRuntimeData: {
			absoluteComps: [],
		},
		shouldRenderWixAdsOnNavigation: freemiumBannerParam === 'true',
		shouldShowCoBrandingBanner: coBrandingBannerParam === 'true',
		shouldShowDayfulBanner: dayfulBannerParam === 'true',
		shouldShowMobileActionsMenu: mobileActionsMenuParam === 'true',
		isPremiumDomain: isPremiumDomain === 'true',
		clientSpecMap,
		componentsMappers: mergeMappers(componentsMappers, (name: keyof BeckyModel['experiments']) =>
			experiments.hasOwnProperty(name)
		),
		experiments,
		routersInfo: routersInfo!,
		deviceType: deviceType!,
		qaMode: qaMode === 'true',
		testMode: testMode === 'true',
		debugMode: debugMode === 'true',
		isInSeo,
		migratingToOoiWidgetIds: migratingToOoiWidgetIds ? migratingToOoiWidgetIds.split(',') : [],
		checkoutOOI: checkoutOOI === 'true',
		stylableItemsCss: stylableItemsCss || {},
		stylableInstance,
		themeCounter: 0,
		enableVariantsTransitionsInEditor: true,
		compIdToIgnoredTransformProps: {},
		globalIgnoredTransformProps: [],
		isHttps: isHttps === 'true',
		shapeStore,
		compsInFoldMap,
		imageCompsTargetScale: 1,
		formFactor,
		anywhereThemeOverride,
		activeVariant: '',
		globalTranslations,
		ooiCssConfigStore: {},
		isConsentPolicyActive: isConsentPolicyActive || false,
	}
}
